import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { showSuccess, showError } from "@/lib/toastHelpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, X } from "lucide-react";

const UNITS = ["minutes", "hours", "days"];

const ACTION_TYPES = [
  { value: "enroll_sequence", label: "Enroll user into a sequence", needsSeq: true, desc: "Adds the user to the selected sequence. First email sends immediately, subsequent steps follow configured delays." },
  { value: "send_immediately", label: "Send sequence immediately (skip delays)", needsSeq: true, desc: "Enrolls the user and sends all emails right now, ignoring step delays. Useful for testing." },
  { value: "unsubscribe_sequence", label: "Unsubscribe from a specific sequence", needsSeq: true, desc: "Cancels the user's active enrollment in the selected sequence only." },
  { value: "unsubscribe_all", label: "Unsubscribe from all lists & sequences", needsSeq: false, desc: "Removes the user from all distribution lists and cancels all active sequence enrollments." },
];

const CONDITION_TYPES = [
  { value: "days_since_signup", label: "Days since signup", hasValue: true, desc: "Triggers after the user has been registered for the specified number of days." },
  { value: "days_since_last_login", label: "Days since last login", hasValue: true, desc: "Triggers when the user hasn't logged in for the specified number of days." },
  { value: "days_since_purchase", label: "Days since last purchase", hasValue: true, desc: "Triggers when the user hasn't made a purchase for the specified number of days." },
  { value: "never_purchased", label: "Never made a purchase", hasValue: false, desc: "Triggers for users who have registered but never completed a purchase." },
];

const emptyCondition = () => ({ type: "", value: 1, unit: "days" });
const emptyForm = () => ({
  name: "",
  conditionOperator: "AND",
  conditions: [emptyCondition()],
  action: { type: "enroll_sequence", sequenceId: "" },
  status: "active",
});

const statusBadge = (s: string) => s === "active" ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" : "bg-muted text-foreground";

export default function TriggersTab() {
  const queryClient = useQueryClient();
  const [view, setView] = useState<"rules" | "logs">("rules");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [form, setForm] = useState<any>(emptyForm());

  const { data: rules = [], isLoading } = useQuery({
    queryKey: ["/api/email-mgmt/triggers"],
    queryFn: () => apiRequest("GET", "/api/email-mgmt/triggers"),
  });
  const { data: logs = [] } = useQuery({
    queryKey: ["/api/email-mgmt/triggers/logs"],
    queryFn: () => apiRequest("GET", "/api/email-mgmt/triggers/logs"),
  });
  const { data: sequences = [] } = useQuery({
    queryKey: ["/api/email-mgmt/sequences"],
    queryFn: () => apiRequest("GET", "/api/email-mgmt/sequences"),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/api/email-mgmt/triggers"] });

  const openCreate = () => { setSelected(null); setForm(emptyForm()); setDialogOpen(true); };
  const openEdit = (r: any) => {
    setSelected(r);
    setForm({
      name: r.name,
      conditionOperator: r.conditionOperator || "AND",
      conditions: r.conditions?.length ? r.conditions : [emptyCondition()],
      action: r.action || { type: "enroll_sequence", sequenceId: "" },
      status: r.status,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (selected) await apiRequest("PUT", `/api/email-mgmt/triggers/${selected._id}`, form);
      else await apiRequest("POST", "/api/email-mgmt/triggers", form);
      showSuccess(selected ? "Rule updated" : "Rule created");
      setDialogOpen(false);
      invalidate();
    } catch { showError("Failed to save"); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this rule?")) return;
    try { await apiRequest("DELETE", `/api/email-mgmt/triggers/${id}`); showSuccess("Deleted"); invalidate(); }
    catch { showError("Failed to delete"); }
  };

  const toggleStatus = async (r: any) => {
    const s = r.status === "active" ? "inactive" : "active";
    try { await apiRequest("PUT", `/api/email-mgmt/triggers/${r._id}`, { status: s }); invalidate(); }
    catch { showError("Failed"); }
  };

  const addCondition = () => setForm((p: any) => ({ ...p, conditions: [...p.conditions, emptyCondition()] }));
  const removeCondition = (i: number) => setForm((p: any) => ({ ...p, conditions: p.conditions.filter((_: any, idx: number) => idx !== i) }));
  const updateCondition = (i: number, field: string, val: any) =>
    setForm((p: any) => ({ ...p, conditions: p.conditions.map((c: any, idx: number) => idx === i ? { ...c, [field]: val } : c) }));

  const condLabel = (type: string) => CONDITION_TYPES.find(t => t.value === type)?.label || type;
  const hasValue = (type: string) => CONDITION_TYPES.find(t => t.value === type)?.hasValue !== false;
  const seqName = (id: string) => (sequences as any[]).find(s => s._id === id)?.name || id;

  return (
    <div>
      <div className="flex gap-4 mb-5 border-b">
        {(["rules", "logs"] as const).map(k => (
          <button key={k} onClick={() => setView(k)}
            className={`pb-2 text-sm font-medium border-b-2 transition-colors capitalize ${view === k ? "border-blue-600 text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {k === "rules" ? "Custom Rules" : "Audit Log"}
          </button>
        ))}
      </div>

      {isLoading ? <p className="text-center text-gray-400 py-8">Loading...</p> : view === "rules" ? (
        <>
          <div className="flex justify-between items-center mb-4">
            <p className="text-muted-foreground text-sm">{(rules as any[]).length} rule{(rules as any[]).length !== 1 ? "s" : ""}</p>
            <Button onClick={openCreate} size="sm" className="flex items-center gap-2"><Plus size={16} /> New Rule</Button>
          </div>
          {(rules as any[]).length === 0 ? <p className="text-center text-muted-foreground py-8">No rules yet</p> : (
            <div className="space-y-3">
              {(rules as any[]).map((r: any) => (
                <div key={r._id} className="border rounded-lg p-4 hover:shadow-xs">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 mr-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold text-sm">{r.name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${statusBadge(r.status)}`}>{r.status}</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mb-2">
                        {(r.conditions || []).map((c: any, i: number) => (
                          <span key={i} className="text-xs bg-muted text-foreground px-2 py-0.5 rounded">
                            {i > 0 && <span className="text-primary font-semibold mr-1">{r.conditionOperator}</span>}
                            {condLabel(c.type)}{hasValue(c.type) ? ` — ${c.value} ${c.unit}` : ""}
                          </span>
                        ))}
                      </div>
                      {r.action && (
                        <p className="text-xs text-muted-foreground">
                          → {ACTION_TYPES.find(a => a.value === r.action.type)?.label || r.action.type}
                          {r.action.sequenceId && `: `}
                          {r.action.sequenceId && <span className="font-medium">{seqName(r.action.sequenceId)}</span>}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => toggleStatus(r)}>
                        {r.status === "active" ? "Pause" : "Activate"}
                      </Button>
                      <Button size="sm" className="text-xs h-7" onClick={() => openEdit(r)}>Edit</Button>
                      <Button size="sm" variant="destructive" className="text-xs h-7" onClick={() => handleDelete(r._id)}>Delete</Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <p className="text-muted-foreground text-sm mb-4">{(logs as any[]).length} recent event{(logs as any[]).length !== 1 ? "s" : ""}</p>
          {(logs as any[]).length === 0 ? <p className="text-center text-muted-foreground py-8">No trigger events yet</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-muted-foreground text-xs">
                  <th className="pb-2 pr-4">User</th>
                  <th className="pb-2 pr-4">Trigger</th>
                  <th className="pb-2 pr-4">Sequence</th>
                  <th className="pb-2">Fired At</th>
                </tr></thead>
                <tbody>
                  {(logs as any[]).map((l: any) => (
                    <tr key={l._id} className="border-b hover:bg-muted/50">
                      <td className="py-2 pr-4 text-xs">{l.email}</td>
                      <td className="py-2 pr-4 text-xs"><span className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 px-2 py-0.5 rounded-full">{l.triggerType}</span></td>
                      <td className="py-2 pr-4 text-xs">{l.sequenceName}</td>
                      <td className="py-2 text-xs text-muted-foreground">{new Date(l.firedAt).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{selected ? "Edit" : "Create"} Rule</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label>Rule Name *</Label>
              <Input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1" placeholder="e.g. Re-engage inactive users" />
            </div>

            {/* Conditions */}
            <div className="border rounded-lg p-4 bg-muted/50 space-y-3">
              <div className="flex justify-between items-center">
                <Label>Conditions</Label>
                {form.conditions.length > 1 && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Match:</span>
                    {["AND", "OR"].map(op => (
                      <button key={op} type="button"
                        onClick={() => setForm((p: any) => ({ ...p, conditionOperator: op }))}
                        className={`px-3 py-1 rounded text-xs font-semibold border transition-colors ${form.conditionOperator === op ? "bg-blue-600 text-white border-blue-600" : "bg-background text-muted-foreground border-border"}`}>
                        {op}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {form.conditions.map((c: any, i: number) => (
                <div key={i} className="bg-background border rounded-lg p-3 space-y-2">
                  {i > 0 && <span className="text-xs font-semibold text-primary">{form.conditionOperator}</span>}
                  <div className="flex gap-2 items-start">
                    <div className="flex-1 space-y-2">
                      <select value={c.type} onChange={e => updateCondition(i, "type", e.target.value)}
                        className="w-full border rounded px-2 py-1.5 text-sm bg-background focus:outline-hidden">
                        <option value="">— Select condition —</option>
                        {CONDITION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                      {c.type && CONDITION_TYPES.find(t => t.value === c.type)?.desc && (
                        <p className="text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded px-2 py-1.5">
                          {CONDITION_TYPES.find(t => t.value === c.type)!.desc}
                        </p>
                      )}
                      {hasValue(c.type) && c.type && (
                        <div className="flex gap-2 items-center">
                          <span className="text-xs text-muted-foreground whitespace-nowrap">After</span>
                          <Input type="number" min="1" value={c.value}
                            onChange={e => updateCondition(i, "value", parseInt(e.target.value) || 1)}
                            className="w-20 h-8 text-sm" />
                          <select value={c.unit} onChange={e => updateCondition(i, "unit", e.target.value)}
                            className="border rounded px-2 py-1.5 text-sm bg-background focus:outline-hidden">
                            {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                          </select>
                        </div>
                      )}
                    </div>
                    {form.conditions.length > 1 && (
                      <button type="button" onClick={() => removeCondition(i)} className="text-red-400 hover:text-red-600 mt-1"><X size={16} /></button>
                    )}
                  </div>
                </div>
              ))}
              <button type="button" onClick={addCondition} className="text-xs text-primary hover:underline flex items-center gap-1">
                <Plus size={13} /> Add another condition
              </button>
            </div>

            {/* Action */}
            <div className="border rounded-lg p-4 bg-muted/50 space-y-3">
              <Label>Action</Label>
              <div className="bg-background border rounded-lg p-3 space-y-2">
                <select value={form.action.type}
                  onChange={e => setForm((p: any) => ({ ...p, action: { type: e.target.value, sequenceId: "" } }))}
                  className="w-full border rounded px-2 py-1.5 text-sm bg-background focus:outline-hidden">
                  {ACTION_TYPES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
                {ACTION_TYPES.find(a => a.value === form.action.type)?.desc && (
                  <p className="text-xs text-green-700 bg-green-50 border border-green-100 rounded px-2 py-1.5">
                    {ACTION_TYPES.find(a => a.value === form.action.type)!.desc}
                  </p>
                )}
                {ACTION_TYPES.find(a => a.value === form.action.type)?.needsSeq && (
                  <select value={form.action.sequenceId || ""}
                    onChange={e => setForm((p: any) => ({ ...p, action: { ...p.action, sequenceId: e.target.value } }))}
                    className="w-full border rounded px-2 py-1.5 text-sm bg-background focus:outline-hidden">
                    <option value="">— Select sequence —</option>
                    {(sequences as any[]).map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                  </select>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2 border-t">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit">{selected ? "Update" : "Create"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
